import React from 'react';
import './Statement.style.css';
import StatementProps from './interface';
import StatementItem from './StatementItem';
import dayjs from 'dayjs';
import { stringToMoneyFormat } from 'utils';
import { Avatar } from 'antd';
import { PictureOutlined } from '@ant-design/icons';

const DATE_FORMAT: string = 'YYYY.MM.DD HH:mm:ss';

const Statement: React.FC<StatementProps> = (props) => {
  /* ===== Route ===== */
  /* ===== Props ===== */
  const {
    orderNo = '', // 주문 번호
    orderDate = 0, // 주문 일자
    orderList, // 주문내역
    cancelList, // 취소내역
    exchangeList, // 교환내역
    returnList, // 반품내역
    paymentInfo,
    cashBillInfo,
    ordererInfo, // 주문자 정보
    recipientInfo, // 수령자 정보
  } = props;

  /* ===== Data ===== */

  /* ===== State ===== */
  const printRef = React.useRef<HTMLDivElement>(null);
  const newWindowRef = React.useRef<Window | null>(null);

  /* ===== Hooks ===== */
  /* ===== Function ===== */
  /**
   * 명세서 출력
   * --
   * @returns
   */
  const handlePrint = () => {
    if (!printRef.current) {
      console.error(
        '인쇄할 내용이 없습니다. printRef가 올바르게 설정되었는지 확인하세요.'
      );
      return;
    }

    newWindowRef.current = window.open(
      '',
      '_blank'
      // 'width=100%,height=100%,scrollbars=yes'
    );

    if (newWindowRef.current) {
      const newWindowDocument = newWindowRef.current.document;
      const newWindow = newWindowRef.current;

      const styleAndLinkTags = Array.from(
        document.head.querySelectorAll('link[rel="stylesheet"], style')
      )
        .map((tag) => tag.outerHTML)
        .join('');

      newWindowDocument.writeln(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>주문상세정보</title>
          ${styleAndLinkTags}
        </head>
        <body>
          <div id="print-content-container"></div>
        </body>
        </html>
      `);
      newWindowDocument.close();

      // HTML 내용을 새 창으로 복사
      const printContentContainer = newWindowDocument.getElementById(
        'print-content-container'
      );

      if (printContentContainer) {
        printContentContainer.innerHTML = printRef.current.outerHTML;

        setTimeout(() => {
          newWindow.focus(); // 새 창에 포커스
          newWindow.print(); // 새 창에서 인쇄 대화 상자 열기

          // 인쇄 후 새 창 자동 닫기
          newWindow.close();
        }, 500); // 0.5초 대기
      } else {
        console.error(
          '새 창에서 인쇄 내용을 담을 컨테이너를 찾을 수 없습니다.'
        );
        newWindow.close();
      }

      newWindow.onbeforeunload = () => {
        newWindowRef.current = null;
      };
    }
  };

  /* ===== Variable ===== */
  /* ===== Hooks ===== */
  /* ===== Render ===== */
  return (
    <div className="statement-layout">
      <button className="statement-print-button" onClick={handlePrint}>
        인쇄
      </button>

      <div ref={printRef} className="statement-container">
        {/* SECTION - 주문상세정보 */}
        <StatementItem title="주문상세정보" gap={20}>
          <ul className="statement-item-layout statement-item-list">
            <li>
              <p>
                <span>주문번호</span>: {orderNo}
              </p>
            </li>
            <li>
              <p>
                <span>주문일자</span>:{' '}
                {dayjs.unix(orderDate).format('YYYY-MM-DD HH:mm:ss')}
              </p>
            </li>
          </ul>
        </StatementItem>
        {/* !SECTION - 주문상세정보 */}

        {/* SECTION - 주문내역 */}
        <StatementItem
          title="주문내역"
          gap={20}
          count={orderList?.products?.length ?? 0}
        >
          <table className="statement-item-layout statement-item-table">
            {/* <colgroup>
              <col width={'25%'} />
              <col width={'25%'} />
              <col width={'25%'} />
              <col width={'25%'} />
            </colgroup> */}
            <tr>
              <th>공급사</th>
              <th>상품명/옵션</th>
              <th>수량</th>
              <th>판매가</th>
              <th>상품구매금액</th>
              <th>할인금액</th>
              <th>배송비</th>
              <th>주문상태</th>
            </tr>
            {orderList && orderList.products.length ? (
              orderList?.products.map((item) => (
                <tr>
                  <td>{item.supplier}</td>
                  <td>
                    <div className="statement-product-info">
                      <Avatar
                        shape="square"
                        src={item.thumbnail}
                        icon={<PictureOutlined />}
                        size={52}
                      />
                      <div className="statement-product-info-text">
                        <p>{item.name}</p>
                        <p>
                          <small>{item.option ?? '-'}</small>
                        </p>
                      </div>
                    </div>
                  </td>
                  <td>{stringToMoneyFormat(item.num)}</td>
                  <td>{stringToMoneyFormat(item.originPrice)}</td>
                  <td>{stringToMoneyFormat(item.paymentPrice)}</td>
                  <td>{stringToMoneyFormat(item.discountPrice)}</td>
                  <td>{stringToMoneyFormat(item.deliveryPrice)}</td>
                  <td>{item.deliveryStatus}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={8} className="statement-empty">
                  주문관련 내역이 없습니다.
                </td>
              </tr>
            )}

            <tr>
              <th colSpan={2} className="statement-empty">
                계
              </th>
              <th>{stringToMoneyFormat(orderList?.totalNum)}</th>
              <th>{stringToMoneyFormat(orderList?.totalOriginPrice)}</th>
              <th>{stringToMoneyFormat(orderList?.totalPaymentPrice)}</th>
              <th>{stringToMoneyFormat(orderList?.totalDiscountPrice)}</th>
              <th colSpan={2}></th>
            </tr>
          </table>
        </StatementItem>
        {/* !SECTION - 주문내역 */}

        {/* SECTION - 취소/환불 */}
        <StatementItem
          title="취소/환불"
          gap={20}
          count={cancelList?.products?.length ?? 0}
        >
          <table className="statement-item-layout statement-item-table">
            <tr>
              <th>공급사</th>
              <th>상품명/옵션</th>
              <th>수량</th>
              <th>판매가 (상품구매금액)</th>
              <th>환불금액</th>
              <th>환불수량</th>
              <th>환불내역</th>
            </tr>
            {cancelList && cancelList.products.length ? (
              cancelList?.products.map((item) => (
                <tr>
                  <td>{item.supplier}</td>
                  <td>
                    <div className="statement-product-info">
                      <Avatar
                        shape="square"
                        src={item.thumbnail}
                        icon={<PictureOutlined />}
                        size={52}
                      />
                      <div className="statement-product-info-text">
                        <p>{item.name}</p>
                        <p>
                          <small>{item.option ?? '-'}</small>
                        </p>
                      </div>
                    </div>
                  </td>
                  <td>{stringToMoneyFormat(item.num)}</td>
                  <td>
                    {stringToMoneyFormat(item.originPrice)}
                    <br />({stringToMoneyFormat(item.paymentPrice)})
                  </td>
                  <td>{stringToMoneyFormat(item.refundAmount)}</td>
                  <td>{stringToMoneyFormat(item.refundNum)}</td>
                  <td>
                    <div>
                      <p>
                        <strong>환불상태 :</strong>{' '}
                        <span>{item.refundStatus}</span>
                      </p>
                      <p>
                        <strong>환불일 :</strong>{' '}
                        <span>
                          {dayjs.unix(item.refundDate).format(DATE_FORMAT)}
                        </span>
                      </p>
                      <p>
                        <strong>환불사유 :</strong>{' '}
                        <span>{item.refundReason}</span>
                      </p>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={8} className="statement-empty">
                  취소/환불관련 내역이 없습니다.
                </td>
              </tr>
            )}

            <tr>
              <th colSpan={2} className="statement-empty">
                계
              </th>
              <th>{stringToMoneyFormat(cancelList?.totalNum)}</th>
              <th>
                {stringToMoneyFormat(cancelList?.totalOriginPrice)}
                <br />({stringToMoneyFormat(cancelList?.totalPaymentPrice)})
              </th>

              <th>{stringToMoneyFormat(cancelList?.totalRefundPrice)}</th>
              <th>{stringToMoneyFormat(cancelList?.totalRefundNum)}</th>
              <th></th>
            </tr>
          </table>
        </StatementItem>
        {/* !SECTION - 취소/환불 */}

        {/* SECTION - 교환 */}
        <StatementItem
          title="교환"
          gap={20}
          count={exchangeList?.products?.length ?? 0}
        >
          <table className="statement-item-layout statement-item-table">
            <tr>
              <th>공급사</th>
              <th>상품명/옵션</th>
              <th>수량</th>
              <th>판매가</th>
              <th>상품구매금액</th>
              <th>할인금액</th>
              <th>배송비</th>
              <th>주문상태</th>
            </tr>
            {exchangeList && exchangeList.products.length ? (
              exchangeList?.products.map((item) => (
                <tr>
                  <td>{item.supplier}</td>
                  <td>
                    <div className="statement-product-info">
                      <Avatar
                        shape="square"
                        src={item.thumbnail}
                        icon={<PictureOutlined />}
                        size={52}
                      />
                      <div className="statement-product-info-text">
                        <p>{item.name}</p>
                        <p>
                          <small>{item.option ?? '-'}</small>
                        </p>
                      </div>
                    </div>
                  </td>
                  <td>{stringToMoneyFormat(item.num)}</td>
                  <td>{stringToMoneyFormat(item.originPrice)}</td>
                  <td>{stringToMoneyFormat(item.paymentPrice)}</td>
                  <td>{stringToMoneyFormat(item.discountPrice)}</td>
                  <td>{stringToMoneyFormat(item.deliveryPrice)}</td>
                  <td>{item.deliveryStatus}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={8} className="statement-empty">
                  교환관련 내역이 없습니다.
                </td>
              </tr>
            )}

            <tr>
              <th colSpan={2} className="statement-empty">
                계
              </th>
              <th>{stringToMoneyFormat(orderList?.totalNum)}</th>
              <th>{stringToMoneyFormat(orderList?.totalOriginPrice)}</th>
              <th>{stringToMoneyFormat(orderList?.totalPaymentPrice)}</th>
              <th>{stringToMoneyFormat(orderList?.totalDiscountPrice)}</th>
              <th colSpan={2}></th>
            </tr>
          </table>
        </StatementItem>
        {/* !SECTION - 교환 */}

        {/* SECTION - 반품 */}
        <StatementItem
          title="반품"
          gap={20}
          count={returnList?.products?.length ?? 0}
        >
          <table className="statement-item-layout statement-item-table">
            <tr>
              <th>공급사</th>
              <th>상품명/옵션</th>
              <th>수량</th>
              <th>판매가</th>
              <th>상품구매금액</th>
              <th>할인금액</th>
              <th>배송비</th>
              <th>주문상태</th>
            </tr>
            {returnList && returnList.products.length ? (
              returnList?.products.map((item) => (
                <tr>
                  <td>{item.supplier}</td>
                  <td>
                    <div className="statement-product-info">
                      <Avatar
                        shape="square"
                        src={item.thumbnail}
                        icon={<PictureOutlined />}
                        size={52}
                      />
                      <div className="statement-product-info-text">
                        <p>{item.name}</p>
                        <p>
                          <small>{item.option ?? '-'}</small>
                        </p>
                      </div>
                    </div>
                  </td>
                  <td>{stringToMoneyFormat(item.num)}</td>
                  <td>{stringToMoneyFormat(item.originPrice)}</td>
                  <td>{stringToMoneyFormat(item.paymentPrice)}</td>
                  <td>{stringToMoneyFormat(item.discountPrice)}</td>
                  <td>{stringToMoneyFormat(item.deliveryPrice)}</td>
                  <td>{item.deliveryStatus}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={8} className="statement-empty">
                  반품관련 내역이 없습니다.
                </td>
              </tr>
            )}

            <tr>
              <th colSpan={2} className="statement-empty">
                계
              </th>
              <th>{stringToMoneyFormat(orderList?.totalNum)}</th>
              <th>{stringToMoneyFormat(orderList?.totalOriginPrice)}</th>
              <th>{stringToMoneyFormat(orderList?.totalPaymentPrice)}</th>
              <th>{stringToMoneyFormat(orderList?.totalDiscountPrice)}</th>
              <th colSpan={2}></th>
            </tr>
          </table>
        </StatementItem>
        {/* !SECTION - 반품 */}

        {/* SECTION - 결제정보 */}

        <StatementItem title="결제정보" gap={20}>
          <table className="statement-item-layout statement-item-table">
            <colgroup>
              <col width={'20%'} />
              <col width={'30%'} />
              <col width={'20%'} />
              <col width={'30%'} />
            </colgroup>

            {paymentInfo ? (
              <>
                <tr>
                  <th>승인번호 (TID)</th>
                  <td>
                    {paymentInfo.ordno} ({paymentInfo.id})
                  </td>
                  <th>결제일</th>
                  <td>{paymentInfo.date}</td>
                </tr>
                <tr>
                  <th>결제수단</th>
                  <td>{paymentInfo.method}</td>
                  <th>결제금액</th>
                  <td>{stringToMoneyFormat(paymentInfo.amount)}</td>
                </tr>
              </>
            ) : (
              <tr>
                <td colSpan={4} className="statement-empty">
                  결제정보가 없습니다
                </td>
              </tr>
            )}
          </table>
        </StatementItem>
        {/* !SECTION - 결제정보 */}

        {/* SECTION - 환불정보 */}
        {/* !SECTION - 환불정보 */}

        {/* SECTION - 현금영수증 발급 내역 */}
        <StatementItem title="현금영수증 발급 내역" gap={20}>
          <table className="statement-item-layout statement-item-table">
            <colgroup>
              <col width={'25%'} />
              <col width={'25%'} />
              <col width={'25%'} />
              <col width={'25%'} />
            </colgroup>
            <tr>
              <th>승인번호</th>
              <th>승인코드</th>
              <th>신청일</th>
              <th>신청금액</th>
            </tr>
            <tr>
              {cashBillInfo ? (
                <>
                  <td>{cashBillInfo.id}</td>
                  <td>{cashBillInfo.code}</td>
                  <td>{cashBillInfo.date}</td>
                  <td>{cashBillInfo.amount}</td>
                </>
              ) : (
                <td colSpan={4} className="statement-empty">
                  현금영수증 발급 내역이 없습니다.
                </td>
              )}
            </tr>
          </table>
        </StatementItem>
        {/* !SECTION - 현금영수증 발급 내역 */}

        {/* SECTION - 세금계산서 발급 내역 */}
        {/* <StatementItem title="세금계산서 발급 내역" gap={20}>
          <table className="statement-item-layout statement-item-table">
            <colgroup>
              <col width={'25%'} />
              <col width={'25%'} />
              <col width={'25%'} />
              <col width={'25%'} />
            </colgroup>
            <tr>
              <th>승인번호</th>
              <th>승인코드</th>
              <th>신청일</th>
              <th>신청금액</th>
            </tr>
            <tr>
              {cashBillInfo ? (
                <>
                  <td>{cashBillInfo.id}</td>
                  <td>{cashBillInfo.code}</td>
                  <td>{cashBillInfo.date}</td>
                  <td>{cashBillInfo.amount}</td>
                </>
              ) : (
                <td colSpan={4} className="statement-empty">세금계산서 발급 내역이 없습니다.</td>
              )}
            </tr>
          </table>
        </StatementItem> */}
        {/* !SECTION - 세금계산서 발급 내역 */}

        {/* SECTION - 주문자정보 */}
        <StatementItem title="주문자정보" gap={20}>
          <table className="statement-item-layout statement-item-table">
            <colgroup>
              <col width={'20%'} />
              <col width={'30%'} />
              <col width={'20%'} />
              <col width={'30%'} />
            </colgroup>
            <tr>
              <th>주문자명(ID)</th>
              <td>
                {ordererInfo.name}({ordererInfo.id})
              </td>
              <th>성별</th>
              <td>{ordererInfo.gender ?? '-'}</td>
            </tr>
            <tr>
              <th>연락처</th>
              <td>{ordererInfo.phone}</td>
              <th>이메일</th>
              <td>{ordererInfo.email ?? '-'}</td>
            </tr>
          </table>
        </StatementItem>
        {/* !SECTION - 주문자정보 */}

        {/* SECTION - 수령자정보 */}
        <StatementItem title="수령자정보" gap={20}>
          <table className="statement-item-layout statement-item-table">
            <colgroup>
              <col width={'20%'} />
              <col width={'30%'} />
              <col width={'20%'} />
              <col width={'30%'} />
            </colgroup>
            <tr>
              <th>수령자명</th>
              <td>{recipientInfo.name}</td>
              <th>연락처</th>
              <td>{recipientInfo.phone}</td>
            </tr>
            <tr>
              <th>배송지 주소</th>
              <td colSpan={3}>
                ({recipientInfo.postcode}) {recipientInfo.address}
                {', '}
                {recipientInfo.detail}
              </td>
            </tr>
            <tr>
              <th>요청사항</th>
              <td colSpan={3}>{recipientInfo.memo ?? '-'}</td>
            </tr>
          </table>
        </StatementItem>
        {/* !SECTION - 수령자정보 */}
      </div>
    </div>
  );
};

export default Statement;
